import React, { useState } from "react";
import { useSharing } from "../../hooks/useSharing";

interface ShareButtonProps {
  title: string;
  excerpt?: string;
  url: string;
  className?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({ title, excerpt, url, className = "" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const {
    shareNative,
    shareToFacebook,
    shareToTwitter,
    shareToLinkedIn,
    copyToClipboard,
    isNativeShareSupported,
    isSharing,
  } = useSharing();

  const shareText = excerpt ? `${title} - ${excerpt}` : title;
  const fullUrl = window.location.origin + url;

  const handleNativeShare = async () => {
    try {
      await shareNative({
        title,
        text: shareText,
        url: fullUrl,
      });
    } catch (error) {
      console.error("Native share failed:", error);
    }
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(fullUrl);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex items-center space-x-4">
        <span className="text-sm text-slate-400">Share this article:</span>
        <div className="flex space-x-2">
          {/* Native Share Button (if supported) */}
          {isNativeShareSupported && (
            <button
              onClick={handleNativeShare}
              disabled={isSharing}
              className="p-2 text-slate-400 hover:text-yellow-400 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all duration-300 disabled:opacity-50"
              title="Share"
            >
              {isSharing ? (
                <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
              )}
            </button>
          )}

          {/* Facebook */}
          <button
            onClick={() => shareToFacebook(fullUrl)}
            className="p-2 text-slate-400 hover:text-blue-400 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all duration-300"
            title="Share on Facebook"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.77 7.46H14.5v-1.9c0-.9-.02-1.49.49-2.02.51-.52 1.24-.51 2.02-.51h1.48V.5h-2.31c-2.87 0-4.61 1.99-4.61 5.02v1.94H9.5v3.28h2.07V24h4.11V10.74h3.01l.18-3.28z" />
            </svg>
          </button>

          {/* Twitter */}
          <button
            onClick={() => shareToTwitter(shareText, fullUrl)}
            className="p-2 text-slate-400 hover:text-blue-400 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all duration-300"
            title="Share on Twitter"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M23.32 4.56c-.85.38-1.78.64-2.75.76 1-.6 1.76-1.55 2.12-2.68-.93.55-1.96.95-3.06 1.17-.88-.94-2.13-1.53-3.51-1.53-2.66 0-4.81 2.16-4.81 4.81 0 .38.04.75.13 1.1-4-.2-7.57-2.12-9.95-5.04-.42.72-.66 1.55-.66 2.44 0 1.67.85 3.14 2.14 4-.79-.03-1.53-.24-2.18-.6v.06c0 2.33 1.66 4.28 3.86 4.72-.4.11-.83.17-1.27.17-.31 0-.62-.03-.92-.08.62 1.94 2.42 3.35 4.55 3.39-1.67 1.31-3.77 2.09-6.05 2.09-.39 0-.78-.02-1.17-.07 2.18 1.4 4.76 2.21 7.54 2.21 9.05 0 14-7.5 14-14 0-.21 0-.42-.01-.63.96-.69 1.79-1.56 2.45-2.55z" />
            </svg>
          </button>

          {/* LinkedIn */}
          <button
            onClick={() => shareToLinkedIn(fullUrl, title)}
            className="p-2 text-slate-400 hover:text-blue-400 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all duration-300"
            title="Share on LinkedIn"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </button>

          {/* Copy Link */}
          <div className="relative">
            <button
              onClick={handleCopyLink}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className={`p-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all duration-300 ${
                copySuccess ? "text-green-400" : "text-slate-400 hover:text-yellow-400"
              }`}
              title="Copy link"
            >
              {copySuccess ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              )}
            </button>

            {/* Tooltip */}
            {(showTooltip || copySuccess) && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-slate-800 text-white text-xs rounded-lg whitespace-nowrap border border-slate-600 z-10">
                {copySuccess ? "Copied!" : "Copy link"}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareButton;
